import { useState } from 'react';
import { Alert, Button, Modal, SidePanel } from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import { ClaimForm } from './ClaimForm.js';
import { ClaimDetail, Headline } from './ClaimScreen.js';
import { ActivityTab } from './DeskTabs.js';
import { useClaim, useClaimMutations, useClaimPermissions } from './useClaims.js';
import { useActivity, useDeskPermissions } from './useDesk.js';
import { ExpensesTab } from '../expenses/ExpensesTab.js';
import { LaunchExpenseForm } from '../expenses/LaunchExpenseDrawer.js';
import { useCardExpenses, useExpenseGrants } from '../expenses/useClaimExpenses.js';

/** As frases prontas do comentário na ficha — o contato com o associado. */
const QUICK_PHRASES = [
  'Associado ligou pedindo atualização.',
  'Associado enviou documentos pelo chat.',
  'Tentei contato, sem retorno.',
  'Associado informou novo telefone.',
] as const;

type PanelTab = 'ficha' | 'atividade' | 'despesas';

/**
 * A ficha e a correção do sinistro no painel do canto direito, por cima da
 * lista (Sinistros › Sinistros) — pedido do dono em 05/10/2026.
 *
 * O conteúdo é o que já existia: o miolo da ficha (`ClaimDetail`, o mesmo da
 * página `ClaimScreen`) e, ao corrigir, o `ClaimForm` em `editar`, que toma o
 * lugar da ficha dentro do painel como tomava o da página. A página continua
 * existindo, para quem chega por link.
 *
 * **Adicionar um terceiro é criação**, e criação abre no `Modal` do meio da
 * tela, como "Abrir sinistro". Gravado, o painel passa a mostrar o sinistro do
 * terceiro.
 *
 * **Ficha e Atividade em abas.** A Atividade é a mesma da mesa da Análise
 * (`ActivityTab`): comentar aqui aparece lá, e o que se move lá aparece aqui.
 * Para preencher a fase ou mover, é a mesa do departamento.
 *
 * **Despesas do Aviso** (26/10/2026): o Aviso não tem mesa, e o card dele é
 * esta ficha. A aba mostra as despesas lançadas no Aviso deste sinistro, e o
 * formulário de lançar abre dentro dela — a gaveta é peça da mesa.
 *
 * Abrir outro sinistro de dentro do painel (o pai do terceiro, um terceiro da
 * lista) troca o sinistro no lugar, e a correção que estava aberta fecha junto
 * — ela era do outro.
 */
export function ClaimPanel({
  claimId,
  onClose,
  onOpenClaim,
}: {
  claimId: number | null;
  onClose: () => void;
  onOpenClaim: (id: number) => void;
}) {
  return (
    <ClaimPanelBody
      /* Trocar de sinistro zera a correção e o terceiro em andamento. */
      key={claimId ?? 'fechado'}
      claimId={claimId}
      onClose={onClose}
      onOpenClaim={onOpenClaim}
    />
  );
}

function ClaimPanelBody({
  claimId,
  onClose,
  onOpenClaim,
}: {
  claimId: number | null;
  onClose: () => void;
  onOpenClaim: (id: number) => void;
}) {
  const { canEdit } = useClaimPermissions();
  const query = useClaim(claimId);
  const { update } = useClaimMutations();
  const [editing, setEditing] = useState(false);
  const [addingThirdParty, setAddingThirdParty] = useState(false);
  const [tab, setTab] = useState<PanelTab>('ficha');
  const { canComment } = useDeskPermissions(null);
  const activity = useActivity(claimId);
  const expenseGrants = useExpenseGrants('aviso');
  const expenseTab = expenseGrants.launch || expenseGrants.supervise;
  const cardExpenses = useCardExpenses('aviso', claimId, expenseTab);
  const [launching, setLaunching] = useState(false);

  const claim = query.data;
  const viewing = claim && !editing;

  return (
    <>
      <SidePanel
        open={claimId !== null}
        onOpenChange={(open) => !open && onClose()}
        title={
          claim
            ? editing
              ? `Corrigir o sinistro #${claim.id}`
              : `Sinistro #${claim.id}`
            : query.isLoading
              ? 'Carregando…'
              : 'Sinistro'
        }
        descricao={
          claim &&
          (editing ? (
            'Só o que foi contado na abertura. A fase se move pela análise e pelo reparo.'
          ) : (
            <Headline claim={claim} onOpenClaim={onOpenClaim} />
          ))
        }
        actions={
          viewing &&
          canEdit && (
            <>
              <Button variant="contorno" onClick={() => setEditing(true)}>
                Corrigir
              </Button>
              <Button
                variant="contorno"
                loading={
                  update.isPending && update.variables?.body.archived !== undefined
                }
                onClick={() =>
                  update.mutate({ id: claim.id, body: { archived: !claim.archivedAt } })
                }
              >
                {claim.archivedAt ? 'Reativar' : 'Arquivar'}
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-4">
          {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
          {update.error && <Alert tone="erro">{update.error.message}</Alert>}
          {claim && editing && (
            <ClaimForm
              mode={{ kind: 'editar', claim }}
              layer="modal"
              onSaved={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          )}
          {viewing && (
            <Tabs value={tab} onValueChange={(v) => setTab(v as PanelTab)}>
              <TabsList>
                <TabsTrigger value="ficha">Ficha</TabsTrigger>
                <TabsTrigger value="atividade">
                  Atividade
                  {activity.data && (
                    <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
                      {activity.data.length}
                    </span>
                  )}
                </TabsTrigger>
                {expenseTab && (
                  <TabsTrigger value="despesas">
                    Despesas
                    {cardExpenses.data && (
                      <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
                        {cardExpenses.data.expenses.length}
                      </span>
                    )}
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="ficha" className="mt-4 flex flex-col gap-4">
                <ClaimDetail
                  claim={claim}
                  onAddThirdParty={() => setAddingThirdParty(true)}
                  onOpenClaim={onOpenClaim}
                />
              </TabsContent>
              <TabsContent value="atividade" className="mt-4">
                <ActivityTab
                  claimId={claim.id}
                  items={activity.data}
                  loading={activity.isLoading}
                  error={activity.error}
                  canComment={canComment}
                  quickPhrases={QUICK_PHRASES}
                />
              </TabsContent>
              {expenseTab && (
                <TabsContent value="despesas" className="mt-4">
                  {launching ? (
                    <LaunchExpenseForm
                      stage="aviso"
                      originId={claim.id}
                      cardLabel={`Sinistro #${claim.id}`}
                      onClose={() => setLaunching(false)}
                    />
                  ) : (
                    <ExpensesTab
                      stage="aviso"
                      originId={claim.id}
                      onLaunch={() => setLaunching(true)}
                    />
                  )}
                </TabsContent>
              )}
            </Tabs>
          )}
        </div>
      </SidePanel>

      {claim && (
        <Modal
          open={addingThirdParty}
          onOpenChange={setAddingThirdParty}
          size="xl"
          dismissOnOutsideClick={false}
          title={`Terceiro envolvido no #${claim.id}`}
          descricao="O protocolo, quando e onde aconteceu e o reboque vêm do sinistro do associado, e podem ser corrigidos. A versão do terceiro começa vazia."
        >
          <ClaimForm
            mode={{ kind: 'terceiro', parent: claim }}
            layer="modal"
            onSaved={(created) => {
              setAddingThirdParty(false);
              onOpenClaim(created.id);
            }}
            onCancel={() => setAddingThirdParty(false)}
          />
        </Modal>
      )}
    </>
  );
}
